import swedishDeathCleaningImage from '@images/home/celebrity-amy-poehler.jpg';
import hoardersImage from '@images/home/celebrity-hoarders.jpg';
import rachaelRayShowImage from '@images/home/celebrity-rachael-ray.jpg';
import heroPosterDesktop from '@images/home/hero-poster-desktop.jpg';
import heroPosterMobile from '@images/home/hero-poster-mobile.jpg';
import honestRenovationsImage from '@images/home/honest-renovations.jpg';
import netflixBg from '@images/home/netflix-bg.jpg';
import securityFacility from '@images/home/security-1.png';
import securityMonitoring from '@images/home/security-2.jpg';
import securityTrusted from '@images/home/security-3.png';
import stepCatalog from '@images/home/step-catalog.png';
import stepDelivery from '@images/home/step-delivery.png';
import stepPickup from '@images/home/step-pickup.png';
import stepWarehouse from '@images/home/step-warehouse.png';

export type HomeStep = {
  title: string;
  mobileTitle?: string;
  description: string;
  image: string;
  imageAlt: string;
};

export type HomeSecuritySlide = {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
};

export type HomeComparisonRow = {
  feature: string;
  clutter: boolean;
  traditional: boolean;
};

export type HomeTestimonial = {
  quote: string;
  body: string;
  author: string;
};

export type HomeBrandPartner = {
  id: string;
  celebrity: string;
  show: string;
  title: string;
  /** Featured cards (e.g. Netflix) use a longer expanded headline on mobile. */
  expandedTitle?: string;
  description: string;
  image: string;
  videoId?: string;
  featured?: boolean;
};

export type HomeFaqItem = {
  question: string;
  answer: string;
};

/** First frame of the hero videos — used as poster + reduced-motion fallback. */
export const HOME_HERO_IMAGE = heroPosterDesktop;
export const HOME_HERO_IMAGE_MOBILE = heroPosterMobile;

export const HOME_STORAGE_STEPS: HomeStep[] = [
  {
    title: 'Picked up from your door',
    description:
      'Schedule a pickup. Our team arrives, wraps each item, and builds your photo catalog on the spot.',
    image: stepPickup,
    imageAlt: 'Clutter team packing items into branded boxes',
  },
  {
    title: 'Photographed and cataloged',
    description:
      'Your account shows every item, with photos. Know what you have without going anywhere.',
    image: stepCatalog,
    imageAlt: 'Customer browsing their photo inventory in the Clutter app',
  },
  {
    title: 'Protected in a secure facility',
    description:
      'Your things aren’t in a unit somewhere. They’re in a private warehouse, monitored 24/7, accessible only to trained Clutter staff.',
    image: stepWarehouse,
    imageAlt:
      'Hands placing a Clutter by Iron Mountain box in a secure warehouse',
  },
  {
    title: 'Delivered back on request',
    description:
      'One request in your account and we deliver within your chosen window. Reschedule up to 48 hours ahead at no charge.',
    image: stepDelivery,
    imageAlt:
      'Customer relaxing in a newly delivered chair wrapped in protective plastic',
  },
];

export const HOME_SECURITY_SLIDES: HomeSecuritySlide[] = [
  {
    title: 'Authorised entry only, no public access',
    description:
      'Our warehouses are not open to the public. Every employee is background-checked and trained.',
    image: securityFacility,
    imageAlt:
      'Clutter team member holding a Clutter by Iron Mountain box in a secure warehouse',
  },
  {
    title: '24/7 video surveillance',
    description:
      'Facilities are monitored around the clock. All storage and handling areas are covered at all times.',
    image: securityMonitoring,
    imageAlt: 'Monitored storage facility',
  },
  {
    title: 'Trusted with the world’s most valuable assets',
    description:
      'Clean, controlled facilities designed for long-term protection. The Iron Mountain standard, now applied to yours.',
    image: securityTrusted,
    imageAlt:
      'Clutter team member moving Clutter by Iron Mountain boxes with a hand truck',
  },
];

export const HOME_COMPARISON_ROWS: HomeComparisonRow[] = [
  { feature: 'Pickup from your home', clutter: true, traditional: false },
  { feature: 'Packing and wrapping', clutter: true, traditional: false },
  { feature: 'Photo catalog of every item', clutter: true, traditional: false },
  { feature: 'Individual item delivery', clutter: true, traditional: false },
  { feature: 'Transparent monthly pricing', clutter: true, traditional: false },
  { feature: 'No truck rental needed', clutter: true, traditional: false },
];

export const HOME_COMPARISON_ROWS_MOBILE: HomeComparisonRow[] = [
  { feature: 'Pick up from your home', clutter: true, traditional: false },
  { feature: 'Packing and wrapping', clutter: true, traditional: false },
  { feature: 'Photo catalog', clutter: true, traditional: false },
  { feature: 'Item delivery', clutter: true, traditional: false },
  { feature: 'Transparent pricing', clutter: true, traditional: false },
  { feature: 'No truck rental', clutter: true, traditional: false },
];

export const HOME_TESTIMONIALS: HomeTestimonial[] = [
  {
    quote: '“Seamless from start to finish.”',
    body: 'They were super efficient, communicated everything to me clearly step by step, and took very good care of all of my items. I will definitely use Clutter again!',
    author: 'Shari Corbitt',
  },
  {
    quote: '“A+ experience”',
    body: 'Amazing from day one to pickup day. Organized, friendly, efficient, and an A+ experience. A trustworthy company and staff.',
    author: 'Brian Laird',
  },
  {
    quote: '“Easy and transparent from start to finish”',
    body: 'Very easy and transparent process from ordering to completion. Everything is photographed and tagged, and they arrived on time and worked diligently.',
    author: 'Sharon W.',
  },
  {
    quote: '“Super efficient”',
    body: 'My moving team was very attentive, asked questions, and even packed some things for me. They did a great job and were SUPER efficient!',
    author: 'Santrice',
  },
  {
    quote: '“The photo inventory makes it so easy”',
    body: 'I did a lot of research before choosing Clutter. The photo inventory is so helpful! I can get exactly what I need dropped off. The pickup was great and they packed everything so carefully.',
    author: 'Elizabeth',
  },
  {
    quote: '“They literally did everything for me”',
    body: 'Quick and easy booking with no confusing contracts. My moving team kept me updated throughout the entire experience and packed everything with 5-star quality.',
    author: 'Brad Brown',
  },
];

/** Desktop accordion order (matches reference HTML). */
export const HOME_BRAND_PARTNERS_DESKTOP: HomeBrandPartner[] = [
  {
    id: 'honest',
    celebrity: 'Jessica Alba',
    show: 'The Honest Company',
    title: 'Honest Renovations',
    description:
      'Clutter is the official storage and moving partner of Honest Renovations, the home renovation show featuring Jessica Alba and Lizzy Mathis. Eight episodes, airing on The Roku Channel.',
    image: honestRenovationsImage,
  },
  {
    id: 'peacock',
    celebrity: 'Amy Poehler',
    show: 'The Gentle Art of Swedish Death Cleaning',
    title: 'The Gentle Art of Swedish Death Cleaning',
    description:
      'Clutter partners with Amy Poehler on The Gentle Art of Swedish Death Cleaning, a Peacock series following people as they declutter their homes through the Scandinavian practice of döstädning.',
    image: swedishDeathCleaningImage,
  },
  {
    id: 'netflix',
    celebrity: 'Instant Dream Home',
    show: 'Netflix',
    title: 'Instant Dream Home',
    expandedTitle: 'Behind every transformation on Instant Dream Home',
    description:
      'Clutter is the exclusive storage and moving partner of Instant Dream Home, the Netflix show that transforms homes in under 12 hours.',
    image: netflixBg,
    videoId: 'XyJRl54zJdU',
    featured: true,
  },
  {
    id: 'rachael',
    celebrity: 'Rachael Ray',
    show: 'Rachael Ray Show',
    title: 'Rachael Ray Show',
    description:
      'Clutter is featured on upcoming episodes of the Rachael Ray Show, handling storage and moving for the show’s home segments.',
    image: rachaelRayShowImage,
  },
  {
    id: 'hoarders',
    celebrity: 'Hoarders',
    show: 'TV Series',
    title: 'Hoarders',
    description:
      'Clutter partners with Hoarders, the long-running A&E series, to help people across the country sort, store, and reclaim their homes.',
    image: hoardersImage,
  },
];

/** Mobile stack order (Netflix expanded first in reference HTML). */
export const HOME_BRAND_PARTNERS_MOBILE: HomeBrandPartner[] = [
  {
    id: 'netflix',
    celebrity: 'Instant Dream Home',
    show: 'Netflix',
    title: 'Instant Dream Home',
    expandedTitle: 'Behind every transformation on Instant Dream Home',
    description:
      'Clutter is the exclusive storage and moving partner of Instant Dream Home, the Netflix show that transforms homes in under 12 hours.',
    image: netflixBg,
    videoId: 'XyJRl54zJdU',
    featured: true,
  },
  {
    id: 'honest',
    celebrity: 'Jessica Alba',
    show: 'The Honest Company',
    title: 'Honest Renovations',
    description:
      'Clutter is the official storage and moving partner of Honest Renovations, the home renovation show featuring Jessica Alba and Lizzy Mathis. Eight episodes, airing on The Roku Channel.',
    image: honestRenovationsImage,
  },
  {
    id: 'peacock',
    celebrity: 'Amy Poehler',
    show: 'The Gentle Art of Swedish Death Cleaning',
    title: 'The Gentle Art of Swedish Death Cleaning',
    description:
      'Clutter partners with Amy Poehler on The Gentle Art of Swedish Death Cleaning, a Peacock series following people as they declutter their homes through the Scandinavian practice of döstädning.',
    image: swedishDeathCleaningImage,
  },
  {
    id: 'rachael',
    celebrity: 'Rachael Ray',
    show: 'Rachael Ray Show',
    title: 'Rachael Ray Show',
    description:
      'Clutter is featured on upcoming episodes of the Rachael Ray Show, handling storage and moving for the show’s home segments.',
    image: rachaelRayShowImage,
  },
  {
    id: 'hoarders',
    celebrity: 'Hoarders',
    show: 'TV Series',
    title: 'Hoarders',
    description:
      'Clutter partners with Hoarders, the long-running A&E series, to help people across the country sort, store, and reclaim their homes.',
    image: hoardersImage,
  },
];

export const HOME_FAQ_ITEMS: HomeFaqItem[] = [
  {
    question: 'How much storage do I need?',
    answer:
      'Clutter is priced by plan, billed monthly. When you get your quote, we’ll help you estimate the right plan based on what you’re storing. If your needs change on pickup day, we’ll adjust your plan and monthly rate accordingly.',
  },
  {
    question: 'Can I add more items later?',
    answer:
      'Yes. Log into your account, schedule a pickup, and we’ll take care of the rest. Pickups and deliveries are charged per hour.',
  },
  {
    question: 'How do deliveries work?',
    answer:
      'Use your online photo catalog to select the items you want returned and choose a delivery window. Deliveries are charged per hour. For select eligible items, FedEx ground shipping is available nationwide at a flat rate.',
  },
  {
    question: 'Where are your warehouses located?',
    answer:
      'Our warehouses are outside city centers. We operate across the United States and in Toronto, covering most major metropolitan areas.',
  },
  {
    question: 'What features do your warehouses have?',
    answer:
      'Our warehouses are monitored 24/7. Access is restricted to trained Clutter staff only.',
  },
];
